// Pure helpers behind the heatmap cell drill-down.
//
// `fn_get_aim_heatmap_cell` returns
//
//   { run_id, category, provider, category_display, avg_position,
//     top_competitors: [{ park_name, appearances }],
//     mentioned, responses, mention_pct,
//     rows: [{ response_id, prompt_text, client_mentioned, client_mentions_in_answer, errored,
//              list_position, recommendation_strength, … }] }
//
// `rows` holds one row per answer, and `mentioned` / `mention_pct` count the answers that name the
// client, so `mentioned` never exceeds `responses` and the drill's own rate is the one to show.
// `p_limit` caps `rows`, not the counts.

/** Rows the drill-down asks for. */
export const HEATMAP_CELL_LIMIT = 50;

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalize a heatmap-cell payload into what the drill-down renders.
 *
 * @param {object|null|undefined} payload the endpoint payload
 * @param {number} [limit] the row cap the request used
 * @returns {{rows: Array<object>, responseCount: number, truncated: boolean,
 *   responses: number|null, mentioned: number|null, mentionPct: number|null,
 *   avgPosition: number|null, topCompetitors: Array<{park_name: string, appearances: number|null}>}}
 *   `responseCount` is the number of distinct responses among `rows`; `responses`, `mentioned` and
 *   `mentionPct` are the cell's own counts from the payload, null when it lacks them; `truncated` is
 *   true when `rows` filled the cap, so more may exist.
 */
export function heatmapCellView(payload, limit = HEATMAP_CELL_LIMIT) {
  const rows = (Array.isArray(payload?.rows) ? payload.rows : []).filter((r) => r && typeof r === 'object');
  const responseIds = new Set(rows.map((r, i) => r.response_id ?? `row-${i}`));
  const topCompetitors = (Array.isArray(payload?.top_competitors) ? payload.top_competitors : [])
    .filter((c) => c && c.park_name)
    .map((c) => ({ park_name: c.park_name, appearances: toNumberOrNull(c.appearances) }));
  return {
    rows,
    responseCount: responseIds.size,
    truncated: rows.length >= limit,
    responses: toNumberOrNull(payload?.responses),
    mentioned: toNumberOrNull(payload?.mentioned),
    mentionPct: toNumberOrNull(payload?.mention_pct),
    avgPosition: toNumberOrNull(payload?.avg_position),
    topCompetitors,
  };
}
